import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, GeoJSON, Polygon } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  MapPin, Layers, Map, ArrowLeft, Eye, Loader2, AlertTriangle,
  Building2, DollarSign, TrendingUp, TrendingDown, X, ChevronRight, Scale
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const US_CENTER = [39.8, -98.5];
const DEFAULT_ZOOM = 4;

// ISO Region approximate boundaries (simplified polygons)
const ISO_BOUNDARIES = {
  PJM: {
    color: '#3B82F6',
    bounds: [
      [42.5, -84.5], [42.5, -74.5], [38.5, -74.5], [36.5, -76.5], 
      [36.5, -83.5], [39.0, -84.5], [42.5, -84.5]
    ]
  },
  ERCOT: {
    color: '#EF4444',
    bounds: [
      [36.5, -106.5], [36.5, -93.5], [33.5, -93.5], [25.8, -97.5],
      [25.8, -100.5], [29.5, -104.5], [31.5, -106.5], [36.5, -106.5]
    ]
  },
  CAISO: {
    color: '#F59E0B',
    bounds: [
      [42.0, -124.5], [42.0, -114.0], [35.0, -114.0], [32.5, -117.5],
      [32.5, -122.0], [35.0, -121.5], [38.0, -123.5], [42.0, -124.5]
    ]
  },
  NYISO: {
    color: '#8B5CF6',
    bounds: [
      [45.0, -79.5], [45.0, -73.5], [40.5, -73.5], [40.5, -74.5],
      [42.0, -79.5], [45.0, -79.5]
    ]
  },
  ISONE: {
    color: '#10B981',
    bounds: [
      [47.5, -73.5], [47.5, -66.5], [41.0, -66.5], [41.0, -73.5], [47.5, -73.5]
    ]
  },
  MISO: {
    color: '#EC4899',
    bounds: [
      [49.0, -97.0], [49.0, -84.5], [42.5, -84.5], [39.0, -84.5],
      [36.5, -89.5], [33.0, -91.0], [29.0, -94.0], [29.0, -97.0],
      [36.5, -97.0], [43.0, -97.0], [49.0, -97.0]
    ]
  },
  SPP: {
    color: '#06B6D4',
    bounds: [
      [49.0, -104.0], [49.0, -97.0], [36.5, -97.0], [34.0, -100.0],
      [31.5, -103.0], [31.5, -106.5], [37.0, -109.0], [41.0, -109.0],
      [41.0, -104.0], [49.0, -104.0]
    ]
  }
};

const getMarkerColor = (score) => {
  if (score >= 70) return '#229644';
  if (score >= 50) return '#FFA500';
  return '#FF521C';
};

const getMarkerSize = (value) => {
  if (value >= 1000000) return 14;
  if (value >= 500000) return 11;
  if (value >= 200000) return 8;
  return 6;
};

// Component to zoom to state/region
function ZoomToArea({ center, zoom }) {
  const map = useMap();
  
  React.useEffect(() => {
    if (center && zoom) {
      map.flyTo(center, zoom, { duration: 1 });
    }
  }, [center, zoom, map]);
  
  return null;
}

export default function TerritoryMapView() {
  const navigate = useNavigate();
  const [showTerritoryOverlay, setShowTerritoryOverlay] = useState(true);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [zoomTarget, setZoomTarget] = useState(null);

  // Fetch territories data
  const { data: territoriesData } = useQuery({
    queryKey: ['territories'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/territories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch states summary
  const { data: statesData, isLoading: statesLoading } = useQuery({
    queryKey: ['geo-states'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/geo/states`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch contracts for selected state
  const { data: stateContractsData, isLoading: stateContractsLoading } = useQuery({
    queryKey: ['geo-contracts-state', selectedState],
    queryFn: async () => {
      if (!selectedState) return null;
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/geo/contracts/by-state/${selectedState}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    enabled: !!selectedState,
  });

  // Fetch all contracts for territory view
  const { data: geoData, isLoading: geoLoading } = useQuery({
    queryKey: ['geo-contracts'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/geo/contracts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  const territories = territoriesData?.territories || [];
  const states = statesData?.states || [];
  const currentFeatures = selectedState 
    ? (stateContractsData?.features || [])
    : (geoData?.features || []);

  // Handle state selection
  const handleStateClick = (state) => {
    setSelectedState(state.state);
    setSelectedTerritory(null);
    setZoomTarget({
      center: [state.center[1], state.center[0]],
      zoom: 7
    });
  };

  // Handle territory selection
  const handleTerritoryClick = (territory) => {
    setSelectedTerritory(territory.iso_region);
    setSelectedState(null);
    setZoomTarget({
      center: [territory.center[1], territory.center[0]],
      zoom: 5
    });
  };

  // Reset view
  const resetView = () => {
    setSelectedState(null);
    setSelectedTerritory(null);
    setZoomTarget({ center: US_CENTER, zoom: DEFAULT_ZOOM });
  };

  // Filter territories if one is selected
  const filteredFeatures = useMemo(() => {
    if (selectedTerritory) {
      return currentFeatures.filter(f => f.properties.isoRegion === selectedTerritory);
    }
    return currentFeatures;
  }, [currentFeatures, selectedTerritory]);

  const handleViewDetails = (contractId) => {
    navigate(`/contracts/${contractId}`);
  };

  const isLoading = geoLoading || statesLoading;

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="territory-map-view">
      {/* Header */}
      <div className="bg-white border-b p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/map')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <span className="text-gradient-primary">Territory & State Analysis</span>
              </h1>
              <p className="text-sm text-muted-foreground">
                Drill down by ISO region or state to analyze contract distribution
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Compare Regions Button */}
            <Button 
              variant="default" 
              size="sm" 
              onClick={() => navigate('/map/compare')}
              className="bg-[#09409C] hover:bg-[#146CBB]"
            >
              <Scale className="w-4 h-4 mr-1" />
              Compare Regions
            </Button>
            
            {/* Territory Overlay Toggle */}
            <div className="flex items-center gap-2">
              <Switch
                id="territory-overlay"
                checked={showTerritoryOverlay}
                onCheckedChange={setShowTerritoryOverlay}
              />
              <Label htmlFor="territory-overlay" className="text-sm">
                Show ISO Boundaries
              </Label>
            </div>
            
            {(selectedState || selectedTerritory) && (
              <Button variant="outline" size="sm" onClick={resetView}>
                <X className="w-4 h-4 mr-1" />
                Reset View
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Territory/State List */}
        <div className="w-80 border-r bg-white flex flex-col">
          {/* Territory Section */}
          <div className="p-3 border-b">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Layers className="w-4 h-4 text-[#09409C]" />
              ISO Territories
            </h3>
            <div className="space-y-2">
              {territories.map((territory) => (
                <div
                  key={territory.iso_region}
                  className={`p-2 rounded-lg border cursor-pointer transition-all ${
                    selectedTerritory === territory.iso_region 
                      ? 'border-[#09409C] bg-blue-50' 
                      : 'border-gray-200 hover:border-[#09409C]'
                  }`}
                  onClick={() => handleTerritoryClick(territory)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: ISO_BOUNDARIES[territory.iso_region]?.color || '#666' }}
                      />
                      <span className="font-medium text-sm">{territory.iso_region}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{territory.stats?.contractCount || 0} contracts</span>
                    <span>{formatCurrency(territory.stats?.totalValue || 0)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* States Section */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="p-3 border-b">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Map className="w-4 h-4 text-[#09409C]" />
                States
              </h3>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-3 space-y-2">
                {states.map((state) => (
                  <div
                    key={state.state}
                    className={`p-2 rounded-lg border cursor-pointer transition-all ${
                      selectedState === state.state 
                        ? 'border-[#09409C] bg-blue-50' 
                        : 'border-gray-200 hover:border-[#09409C]'
                    }`}
                    onClick={() => handleStateClick(state)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] px-1.5">
                          {state.state}
                        </Badge>
                        <Badge 
                          variant="secondary" 
                          className="text-[10px] px-1.5"
                          style={{ 
                            backgroundColor: `${ISO_BOUNDARIES[state.isoRegion]?.color}20`,
                            color: ISO_BOUNDARIES[state.isoRegion]?.color 
                          }}
                        >
                          {state.isoRegion}
                        </Badge>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                      <div className="flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-gray-400" />
                        <span>{state.stats?.contractCount || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3 text-gray-400" />
                        <span>{formatCurrency(state.stats?.totalValue || 0)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-red-400" />
                        <span>{state.stats?.atRiskCount || 0} at risk</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-orange-400" />
                        <span>{state.stats?.expiringSoon || 0} expiring</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Map Area */}
        <div className="flex-1 relative">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-[#09409C]" />
            </div>
          ) : (
            <MapContainer
              center={US_CENTER}
              zoom={DEFAULT_ZOOM}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {zoomTarget && (
                <ZoomToArea center={zoomTarget.center} zoom={zoomTarget.zoom} />
              )}
              
              {/* ISO Region Boundaries */}
              {showTerritoryOverlay && Object.entries(ISO_BOUNDARIES).map(([region, data]) => (
                <Polygon
                  key={region}
                  positions={data.bounds}
                  pathOptions={{
                    color: data.color,
                    fillColor: data.color,
                    fillOpacity: selectedTerritory === region ? 0.3 : 0.1,
                    weight: selectedTerritory === region ? 3 : 1,
                    dashArray: selectedTerritory === region ? null : '5, 5'
                  }}
                  eventHandlers={{
                    click: () => {
                      const territory = territories.find(t => t.iso_region === region);
                      if (territory) handleTerritoryClick(territory);
                    }
                  }}
                >
                  <Popup>
                    <div className="min-w-[180px]">
                      <div className="font-semibold text-sm mb-1">{region}</div>
                      {territories.find(t => t.iso_region === region) && (
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>Contracts:</span>
                            <span className="font-medium">
                              {territories.find(t => t.iso_region === region)?.stats?.contractCount || 0}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Total Value:</span>
                            <span className="font-medium">
                              {formatCurrency(territories.find(t => t.iso_region === region)?.stats?.totalValue || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>At Risk:</span>
                            <span className="font-medium text-red-600">
                              {territories.find(t => t.iso_region === region)?.stats?.atRiskCount || 0}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Polygon>
              ))}
              
              {/* Contract Markers */}
              {filteredFeatures.map((feature, idx) => {
                const [lng, lat] = feature.geometry.coordinates;
                const props = feature.properties;
                const color = getMarkerColor(props.competitivenessScore);
                const size = getMarkerSize(props.annualValue);
                
                return (
                  <CircleMarker
                    key={props.contractId || idx}
                    center={[lat, lng]}
                    radius={size}
                    pathOptions={{
                      color: color,
                      fillColor: color,
                      fillOpacity: 0.7,
                      weight: 2,
                    }}
                  >
                    <Popup>
                      <div className="min-w-[200px]">
                        <div className="font-semibold text-sm">{props.accountName}</div>
                        <div className="text-xs text-muted-foreground mb-2">{props.contractNumber}</div>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>Value:</span>
                            <span className="font-medium">{formatCurrency(props.annualValue)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Health:</span>
                            <Badge className={`text-[10px] ${props.competitivenessScore >= 70 ? 'bg-green-100 text-green-800' : props.competitivenessScore >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                              {props.competitivenessScore}%
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span>Location:</span>
                            <span>{props.city}, {props.state}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>ISO:</span>
                            <span>{props.isoRegion}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Expires:</span>
                            <span>{props.daysToExpiration} days</span>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          className="w-full mt-2 h-7 text-xs"
                          onClick={() => handleViewDetails(props.contractId)}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View Details
                        </Button>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          )}

          {/* Selected Area Summary */}
          {(selectedState || selectedTerritory) && (
            <div className="absolute top-4 right-4 z-[1000] w-72">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {selectedState ? (
                      <>
                        <Map className="w-4 h-4" />
                        {selectedState} Details
                      </>
                    ) : (
                      <>
                        <Layers className="w-4 h-4" />
                        {selectedTerritory} Territory
                      </>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-xs space-y-2">
                  {selectedState && states.find(s => s.state === selectedState) && (() => {
                    const state = states.find(s => s.state === selectedState);
                    return (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="p-2 bg-gray-50 rounded">
                            <div className="text-muted-foreground">Contracts</div>
                            <div className="font-bold text-lg">{state.stats?.contractCount || 0}</div>
                          </div>
                          <div className="p-2 bg-gray-50 rounded">
                            <div className="text-muted-foreground">Total Value</div>
                            <div className="font-bold text-sm">{formatCurrency(state.stats?.totalValue || 0)}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="p-2 bg-red-50 rounded">
                            <div className="text-red-600">At Risk</div>
                            <div className="font-bold text-lg text-red-700">{state.stats?.atRiskCount || 0}</div>
                          </div>
                          <div className="p-2 bg-orange-50 rounded">
                            <div className="text-orange-600">Expiring Soon</div>
                            <div className="font-bold text-lg text-orange-700">{state.stats?.expiringSoon || 0}</div>
                          </div>
                        </div>
                        <div className="text-muted-foreground pt-1">
                          Cities: {state.cities?.join(', ')}
                        </div>
                      </>
                    );
                  })()}
                  
                  {selectedTerritory && territories.find(t => t.iso_region === selectedTerritory) && (() => {
                    const territory = territories.find(t => t.iso_region === selectedTerritory);
                    return (
                      <>
                        <div className="text-muted-foreground">{territory.name}</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="p-2 bg-gray-50 rounded">
                            <div className="text-muted-foreground">Contracts</div>
                            <div className="font-bold text-lg">{territory.stats?.contractCount || 0}</div>
                          </div>
                          <div className="p-2 bg-gray-50 rounded">
                            <div className="text-muted-foreground">Total Value</div>
                            <div className="font-bold text-sm">{formatCurrency(territory.stats?.totalValue || 0)}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="p-2 bg-red-50 rounded">
                            <div className="text-red-600">At Risk</div>
                            <div className="font-bold text-lg text-red-700">{territory.stats?.atRiskCount || 0}</div>
                          </div>
                          <div className="p-2 bg-blue-50 rounded">
                            <div className="text-blue-600">Avg Health</div>
                            <div className="font-bold text-lg text-blue-700">{territory.stats?.avgCompetitiveness || 0}%</div>
                          </div>
                        </div>
                        <div className="text-muted-foreground pt-1">
                          States: {territory.states?.join(', ')}
                        </div>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Legend */}
          <div className="absolute bottom-4 right-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 text-xs">
            <div className="font-medium mb-2">Contract Health</div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#229644]" />
                <span>Healthy (70%+)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#FFA500]" />
                <span>At Risk (50-69%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#FF521C]" />
                <span>Critical (&lt;50%)</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 text-xs">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-[#09409C]" />
                <span className="font-medium">{filteredFeatures.length}</span>
                <span className="text-muted-foreground">contracts</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="font-medium text-[#229644]">
                  {formatCurrency(filteredFeatures.reduce((sum, f) => sum + f.properties.annualValue, 0))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
